/**
 * PUBLISHER — the boundary between the Lyrixa UI and music distribution.
 *
 * ============================================================================
 * THIS MODULE DOES NOT DISTRIBUTE ANYTHING, AND IT DOES NOT PRETEND TO.
 *
 * Read this before adding a function here. The other modules in this directory
 * are mocks that return fixtures, which is honest for a song list: a fake song
 * in a prototype misleads nobody. Distribution is different in kind. A mock
 * `submitRelease()` that resolved successfully would tell the user their music
 * had been sent to Spotify when nothing left the browser — so the submission
 * calls below REJECT, with the reasons, instead of resolving.
 *
 * That is the §3/§35 instruction taken literally: build the correct boundary,
 * document the backend requirement, do not pretend a release has been submitted.
 * ============================================================================
 *
 * WHAT IS REAL HERE
 * -----------------
 *   - The request bodies. Built by api/labelgrid.ts from the committed OpenAPI spec,
 *     exported, and asserted by tests. `buildSubmissionPlan` below assembles the exact
 *     ordered sequence of LabelGrid calls a submission consists of, and that plan is
 *     inspectable in the UI — the same "show the request" affordance Image Music has.
 *   - The refusals. Every guard that must hold before a submission (entitlement,
 *     validation, payment verification, duplicate protection) is implemented and
 *     tested, because those are the parts that must not be discovered to be missing
 *     the day a backend arrives.
 *   - The catalogue reads. `getOutlets()` prefers the account-specific LabelGrid list and
 *     uses the supported-store prototype catalogue while that account list is unavailable.
 *
 * WHAT IS NOT
 * -----------
 *   - Any HTTP request to LabelGrid. See describeLabelGridBlockers(): four independent
 *     blockers, of which the audio one is not even about LabelGrid.
 *   - Any payment. There is no payment provider in this project; productsApi's own
 *     header says purchases charge nothing.
 *
 * Real endpoints the backend must expose: docs/publisher-backend.md.
 */
import {
  buildReleaseRequest,
  buildTrackRequest,
  canDistribute,
  describeLabelGridBlockers,
  isSubmittable,
  LABELGRID_PATHS,
  type LabelGridBlocker,
  type LabelGridOutlet,
  type LabelGridReleaseRequest,
  type LabelGridTrackRequest,
  type LabelGridValidationResponse,
} from './labelgrid';
import { mockSeedLibrary } from './mockCatalogue';
import { USE_MOCK_API } from '../config';
import { ApiError } from './errors';
import { http } from './http';
import { assertMock, mockDelay } from './mock';
import publisherDashboardMock from '../data/publisherDashboardMock.json';
import publisherAnalyticsMock from '../data/publisherAnalyticsMock.json';
import publisherFinancialMock from '../data/publisherFinancialMock.json';
import publisherPlatformMock from '../data/publisherPlatformMock.json';
import { PUBLISHER_PROTOTYPE_OUTLETS, withoutYouTubeMusic } from '../data/publisherOutlets';
import {
  buildUnavailablePublisherAnalytics,
  clonePublisherAnalyticsPayload,
  isPublisherAnalyticsAllReleasesId,
  PUBLISHER_ANALYTICS_ALL_RELEASES_ID,
  withPublisherAnalyticsRange,
} from '../lib/publisherAnalytics';
import { materializePublisherMockSnapshot } from '../lib/publisherDashboard';
import type {
  CreditBalance,
  PublisherAnalyticsPayload,
  PublisherAnalyticsRangePreset,
  PublisherFinancialExplainer,
  PublisherFinancialPayload,
  PublisherPayment,
  PublisherPaymentCheckoutSession,
  PublisherPlatformMetric,
  PublisherRelease,
  PublisherReleasePlatform,
  PublisherReleasePlatformCatalogEntry,
  PublisherReleasePlatformStats,
  PublisherReleaseType,
  PublisherSettingsPayload,
  PublisherStateSnapshot,
  Song,
  UpdatePublisherSettingsInput,
} from '../types/domain';
import { isUploadedSong } from '../lib/license';

export type PublisherDataMode = 'mock' | 'real';

interface PublisherModeOptions {
  mode?: PublisherDataMode;
}

/**
 * The per-release publishing fee, in whole dollars.
 *
 * SEPARATE FROM THE PUBLISHER PLAN, which is a product in data/products.ts. §17: "Do
 * not automatically include the $12 fee inside the $47 subscription." Two charges, two
 * places in the code, and this is the one that recurs per release.
 */
export const PUBLISHING_FEE = 12;

/** Currency for the fee. */
export const PUBLISHING_FEE_CURRENCY = 'USD';

/**
 * Thrown when distribution is asked for and cannot honestly be performed.
 *
 * Carries the blockers so the UI renders the real reasons rather than a generic
 * failure — the same contract GenerationUnavailableError has for Lyria. A caller that
 * catches this must not report success.
 */
export class DistributionUnavailableError extends Error {
  readonly name = 'DistributionUnavailableError';
  readonly blockers: LabelGridBlocker[];
  /** The plan that WOULD have been executed, for inspection. */
  readonly plan: SubmissionStep[];

  constructor(message: string, blockers: LabelGridBlocker[], plan: SubmissionStep[] = []) {
    super(message);
    this.blockers = blockers;
    this.plan = plan;
  }
}

/** Thrown when a submission is refused because a precondition of the flow is unmet. */
export class SubmissionRefusedError extends Error {
  readonly name = 'SubmissionRefusedError';
  /** Machine-readable reason, so the UI can route the user to the right step. */
  readonly reason: SubmissionRefusalReason;

  constructor(reason: SubmissionRefusalReason, message: string) {
    super(message);
    this.reason = reason;
  }
}

/**
 * Why a submission was refused before any provider call was attempted.
 *
 * Each of these is a rule the brief states, and each has a test. `payment-unverified`
 * is the §17 one: a client-side "paid" is not enough.
 */
export type SubmissionRefusalReason =
  | 'no-entitlement'
  | 'commercial-license-required'
  | 'song-license-unverified'
  | 'rights-attestation-required'
  | 'no-audio'
  | 'validation-failed'
  | 'payment-unverified'
  | 'publisher-credits-insufficient'
  | 'already-submitted';

/**
 * One LabelGrid call in a submission, in the order it must happen.
 *
 * Exported and rendered, because it is the deliverable: with no backend, the honest
 * artefact is the exact sequence the backend has to perform, derived from the real spec
 * rather than described in prose.
 */
export interface SubmissionStep {
  /** Ordinal, from 1. */
  order: number;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  /** Path relative to LABELGRID_BASE_URL. */
  path: string;
  /** What this call accomplishes. */
  purpose: string;
  /** The body, where the step has one and it can be built from local data. */
  body?: LabelGridReleaseRequest | LabelGridTrackRequest | Record<string, string>;
  /**
   * True when the step's inputs are not available in this build.
   *
   * A step is unresolved when it needs a LabelGrid id that only exists in a real
   * account, or a file Lyrixa does not have. Marked rather than filled with a
   * placeholder, so nobody reads the plan as executable.
   */
  unresolved?: string;
}

/**
 * Builds the ordered LabelGrid call sequence for submitting a release.
 *
 * §20 LISTS THIRTEEN POSSIBLE OPERATIONS AND SAYS NOT TO ASSUME ALL ARE REQUIRED. This
 * is the subset the spec actually requires for a release of Lyrixa's shape, in the
 * order the API's own data dependencies force:
 *
 *   1. The release must exist before a track can name its `release_id`.
 *   2. A track must exist before its audio can be uploaded to `/tracks/{track}/files`.
 *   3. Artwork attaches to the release, and can happen any time before validation.
 *   4. Validation must pass before distribution — §14.
 *   5. `distribute` is last, and takes no body: the outlets were set as `dsp_configs`
 *      in step 1. This is the divergence from the brief's assumption; see labelgrid.ts.
 *
 * Deliberately NOT included: `confirm-review`, `takedown-all`, `quality-report/refresh`,
 * `landing-config`, `short-url`. Each exists, none is required to get a release
 * delivered, and adding them would be assuming a sequence the docs do not mandate.
 *
 * Steps whose inputs do not exist are marked `unresolved` rather than omitted, because
 * "what is missing" is the useful information at this stage.
 */
export function buildSubmissionPlan(input: {
  release: PublisherRelease;
  songs: Song[];
}): SubmissionStep[] {
  const { release, songs } = input;
  const steps: SubmissionStep[] = [];

  /*
    Step 1 — create the release, carrying the outlet selection.

    The body is built by the real builder where it can be, and the builder throws when a
    LabelGrid account id is missing. That throw is the finding, not an error to swallow:
    it names exactly which foreign key a real account would have supplied.
  */
  let releaseBody: LabelGridReleaseRequest | undefined;
  let releaseUnresolved: string | undefined;
  try {
    releaseBody = buildReleaseRequest({
      contentType: release.releaseType,
      // These three are the account resources that do not exist here. Passing 0
      // deliberately trips the builder's own guard rather than inventing an id that
      // would look valid in the rendered plan.
      labelId: 0,
      artists: [],
      title: release.title,
      cat: release.id,
      artworkAiUsage: 'all',
      primaryGenreId: 0,
      releaseDate: release.releaseDate || undefined,
      explicit: release.explicit ? 'on' : 'off',
      outlets: release.outletKeys,
    });
  } catch (error) {
    releaseUnresolved =
      error instanceof Error
        ? error.message
        : 'The release body cannot be built without LabelGrid account resources.';
  }
  steps.push({
    order: 1,
    method: 'POST',
    path: LABELGRID_PATHS.releases,
    purpose:
      'Create the release, including dsp_configs — which is where outlet selection ' +
      'travels, because distribute takes no body.',
    body: releaseBody,
    unresolved: releaseUnresolved,
  });

  // Steps 2 and 3, per track: create it, then upload its master.
  let order = 2;
  release.songIds.forEach((songId, index) => {
    const song = songs.find((s) => s.id === songId);
    let trackBody: LabelGridTrackRequest | undefined;
    let trackUnresolved: string | undefined;
    try {
      trackBody = buildTrackRequest({
        // Also 0: the real release id comes from step 1's response and cannot be known
        // before that call happens.
        releaseId: 0,
        trackNum: index + 1,
        artists: [],
        title: song?.title,
        audioLanguage: release.language || 'en',
      });
    } catch (error) {
      trackUnresolved = error instanceof Error ? error.message : 'The track body cannot be built.';
    }
    steps.push({
      order: order++,
      method: 'POST',
      path: LABELGRID_PATHS.tracks,
      purpose: `Create track ${index + 1}${song ? ` — "${song.title}"` : ''}, declaring AI usage.`,
      body: trackBody,
      unresolved: trackUnresolved,
    });

    steps.push({
      order: order++,
      method: 'POST',
      path: LABELGRID_PATHS.trackFileUploadUrl('{track}', 'stereo'),
      purpose: 'Request a pre-signed URL, then PUT the audio master to it.',
      // THE BLOCKING ONE. Not a missing id that credentials would supply — there is no
      // file, because Lyrixa's generation is simulated and audioUrl is null.
      unresolved: song?.audioUrl
        ? undefined
        : `"${song?.title ?? songId}" has no audio file (Song.audioUrl is null — generation is ` +
          'simulated), so there is nothing to deliver.',
    });
  });

  steps.push({
    order: order++,
    method: 'POST',
    path: LABELGRID_PATHS.releaseAssetUploadUrl('{release}', 'square'),
    purpose: 'Request a pre-signed URL for the cover art, then PUT the image to it.',
    unresolved: release.artworkUrl
      ? undefined
      : 'No release artwork has been chosen, and a generated gradient placeholder is not an ' +
        'image file that can be uploaded.',
  });

  steps.push({
    order: order++,
    method: 'POST',
    path: LABELGRID_PATHS.validate('{release}'),
    purpose: 'Validate. Must return result: "OK" with no errors before distributing.',
  });

  steps.push({
    order: order++,
    method: 'POST',
    path: LABELGRID_PATHS.distribute('{release}'),
    purpose:
      'Trigger distribution. NO REQUEST BODY — verified against the spec; outlets were ' +
      'set in step 1.',
  });

  return steps;
}

/**
 * Whether a song can be part of a release at all.
 *
 * §9: "if no real audio asset exists, prevent submission and clearly explain why". This
 * is that check, in one place, returning the explanation rather than a boolean — the UI
 * needs the sentence, and a bare `false` would be reworded slightly differently at each
 * of the three places that ask.
 *
 * Returns null when the song IS deliverable.
 */
export function describeAudioBlocker(song: Song): string | null {
  if (song.audioUrl) return null;
  return (
    `"${song.title}" has no audio file. Lyrixsa's generation is simulated in this build, so ` +
    'songs carry metadata, lyrics and artwork but no master recording — and a distributor ' +
    'cannot accept a release without one.'
  );
}

/** Whether a library song is eligible for distribution through Lyrixsa Publisher. */
export function describePublisherSongBlocker(song: Song): string | null {
  if (isUploadedSong(song)) {
    return 'Uploaded songs cannot be validated for commercial licensing, so they cannot be delivered.';
  }
  return describeAudioBlocker(song);
}

function refusalReasonFromApiError(error: ApiError): SubmissionRefusalReason | null {
  switch (error.code) {
    case 'publisher_no_entitlement':
      return 'no-entitlement';
    case 'publisher_commercial_license_required':
      return 'commercial-license-required';
    case 'publisher_song_license_unverified':
      return 'song-license-unverified';
    case 'publisher_rights_attestation_required':
      return 'rights-attestation-required';
    case 'publisher_no_audio':
      return 'no-audio';
    case 'publisher_validation_failed':
      return 'validation-failed';
    case 'publisher_payment_unverified':
      return 'payment-unverified';
    case 'publisher_already_submitted':
      return 'already-submitted';
    case 'publisher_credits_insufficient':
      return 'publisher-credits-insufficient';
    default:
      return null;
  }
}

function rethrowPublisherBackendError(
  error: unknown,
  input?: { release?: PublisherRelease; songs?: Song[] }
): never {
  if (error instanceof ApiError) {
    const refusalReason = refusalReasonFromApiError(error);
    if (refusalReason) {
      throw new SubmissionRefusedError(refusalReason, error.message);
    }
    if (error.code === 'unsupported_capability' || error.status === 501) {
      const plan =
        input?.release && input?.songs
          ? buildSubmissionPlan({ release: input.release, songs: input.songs })
          : [];
      throw new DistributionUnavailableError(error.message, describeLabelGridBlockers(), plan);
    }
  }
  throw error;
}

type PublisherPlatformMockShape = {
  catalogByReleaseId: Record<string, PublisherReleasePlatformCatalogEntry[]>;
  statsByReleaseId: Record<string, Record<string, PublisherReleasePlatformStats>>;
};

type PublisherAnalyticsMockRelease = {
  defaultPreset: PublisherAnalyticsRangePreset;
  byRange: Partial<Record<PublisherAnalyticsRangePreset, PublisherAnalyticsPayload>>;
};

type PublisherAnalyticsMockShape = {
  releases: Record<string, PublisherAnalyticsMockRelease>;
};

const publisherPlatformFixtures = publisherPlatformMock as PublisherPlatformMockShape;
const publisherAnalyticsFixtures = publisherAnalyticsMock as unknown as PublisherAnalyticsMockShape;
const publisherFinancialFixture = publisherFinancialMock as PublisherFinancialPayload;

function clonePublisherFinancialExplainer(
  explainer: PublisherFinancialExplainer | null | undefined
): PublisherFinancialExplainer | null | undefined {
  if (!explainer) return explainer;
  return {
    ...explainer,
    sections: explainer.sections.map((section) => ({ ...section })),
  };
}

function clonePublisherFinancialPayload(
  payload: PublisherFinancialPayload
): PublisherFinancialPayload {
  return {
    ...payload,
    summary: payload.summary.map((item) => ({ ...item })),
    breakdown: {
      ...payload.breakdown,
      items: payload.breakdown.items.map((item) => ({ ...item })),
      explainer: clonePublisherFinancialExplainer(payload.breakdown.explainer),
    },
    platforms: {
      ...payload.platforms,
      rows: payload.platforms.rows.map((row) => ({ ...row })),
    },
    paymentHistory: {
      ...payload.paymentHistory,
      rows: payload.paymentHistory.rows.map((row) => ({ ...row })),
    },
    education: {
      ...payload.education,
      bullets: [...payload.education.bullets],
      explainer: clonePublisherFinancialExplainer(payload.education.explainer),
    },
    payoutMethod: {
      ...payload.payoutMethod,
    },
  };
}

function titleFromOutletKey(outletKey: string): string {
  return outletKey
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function unavailablePlatformStats(input: {
  releaseId: string;
  outletKey: string;
  outletName?: string;
  note: string;
}): PublisherReleasePlatformStats {
  const metrics: PublisherPlatformMetric[] = [
    {
      key: 'gross-reported',
      label: 'Gross reported',
      value: 'Not reported',
      reported: false,
      kind: 'money',
      note: input.note,
    },
  ];
  return {
    releaseId: input.releaseId,
    outletKey: input.outletKey,
    outletName: input.outletName ?? titleFromOutletKey(input.outletKey),
    source: 'unavailable',
    availability: 'unavailable',
    reportedAt: null,
    metrics,
    rawProviderFields: [],
  };
}

function firstAnalyticsFixture(releaseId: string): PublisherAnalyticsPayload | null {
  const release = publisherAnalyticsFixtures.releases[releaseId];
  if (!release) return null;
  const seeded =
    release.byRange[release.defaultPreset] ?? Object.values(release.byRange).find(Boolean);
  return seeded ? clonePublisherAnalyticsPayload(seeded) : null;
}

function analyticsFixtureFor(input: {
  releaseId: string;
  preset: PublisherAnalyticsRangePreset;
  startDate?: string | null;
  endDate?: string | null;
}): PublisherAnalyticsPayload {
  const release = publisherAnalyticsFixtures.releases[input.releaseId];
  const seeded = release?.byRange[input.preset];
  if (seeded) {
    const payload = clonePublisherAnalyticsPayload(seeded);
    return input.preset === 'custom'
      ? withPublisherAnalyticsRange(payload, {
          preset: input.preset,
          startDate: input.startDate,
          endDate: input.endDate,
        })
      : payload;
  }

  const fallback = firstAnalyticsFixture(input.releaseId);
  const note =
    fallback?.selectedRange.note ??
    'No analytics were seeded for this release in the current Publisher preview.';
  return buildUnavailablePublisherAnalytics({
    releaseId: input.releaseId,
    preset: input.preset,
    startDate: input.startDate,
    endDate: input.endDate,
    reportedAt: fallback?.reportedAt ?? null,
    note,
  });
}

export const publisherApi = {
  /**
   * MOCK: GET /publisher/state
   *
   * The dashboard's read model: releases, payments, events and provider-reported platform
   * rows in one snapshot. In mock mode this is read-only demo data, materialised against the
   * real seed-song ids the process is using, so the Publisher can show a richer dashboard
   * without pretending any submission, payment or validation happened.
   */
  async getState(options: PublisherModeOptions = {}): Promise<PublisherStateSnapshot> {
    const mode = options.mode ?? (USE_MOCK_API ? 'mock' : 'real');
    if (mode === 'mock') {
      if (USE_MOCK_API) assertMock('publisherApi.getState');
      return mockDelay(
        materializePublisherMockSnapshot(
          publisherDashboardMock as PublisherStateSnapshot,
          mockSeedLibrary()
        )
      );
    }
    try {
      return await http.get<PublisherStateSnapshot>('/publisher/state');
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  async getPublisherCredits(): Promise<CreditBalance> {
    try {
      return await http.get<CreditBalance>('/publisher/credits');
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  /**
   * MOCK: GET /publisher/outlets → LabelGrid GET /distro-outlets
   *
   * The live account catalogue remains authoritative when it contains outlets. Until a
   * LabelGrid account catalogue is configured, the UI falls back to the supported-store
   * prototype list so the release-selection flow can be exercised end to end. YouTube
   * Music is removed from both sources for the current rollout.
   *
   * The real response is `LabelGridOutlet[]` — six documented fields, including
   * `is_ai_dsp`, which matters for an all-AI catalogue.
   */
  async getOutlets(): Promise<LabelGridOutlet[]> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.getOutlets');
      return mockDelay([...PUBLISHER_PROTOTYPE_OUTLETS]);
    }
    try {
      const outlets = withoutYouTubeMusic(
        await http.get<LabelGridOutlet[]>('/publisher/outlets')
      );
      return outlets.length > 0 ? outlets : [...PUBLISHER_PROTOTYPE_OUTLETS];
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  /**
   * MOCK: POST /publisher/releases/:id/validate → LabelGrid POST /releases/{release}/validate
   *
   * REJECTS rather than returning a fabricated verdict. §14 says to use the documented
   * validation endpoint "instead of creating a fake validation system", and a mock that
   * returned `result: 'OK'` would be exactly that fake system — it would tell the user
   * their release passed a distributor's checks that were never run.
   *
   * The app still performs its OWN completeness checks locally (see
   * lib/publisherValidation.ts), which is a different and honest thing: those are
   * Lyrixa's requirements for a complete form, clearly labelled as such, and they never
   * claim to be LabelGrid's verdict.
   */
  async validateRelease(
    releaseId: string,
    input?: { release: PublisherRelease; songs: Song[] }
  ): Promise<LabelGridValidationResponse> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.validateRelease');
      const blockers = describeLabelGridBlockers();
      throw new DistributionUnavailableError(
        'This build cannot run LabelGrid validation. ' + blockers.map((b) => b.summary).join(' '),
        blockers
      );
    }
    try {
      return await http.post<LabelGridValidationResponse>(
        `/publisher/releases/${releaseId}/validate`,
        {
          release: input?.release,
          songs: input?.songs ?? [],
        }
      );
    } catch (error) {
      rethrowPublisherBackendError(error, input);
    }
  },

  async generateReleaseArtwork(input: {
    release: PublisherRelease;
    artworkPrompt: string;
  }): Promise<PublisherRelease> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.generateReleaseArtwork');
    }
    try {
      return await http.post<PublisherRelease>(
        `/publisher/releases/${input.release.id}/artwork/generate`,
        {
          release: input.release,
          artworkPrompt: input.artworkPrompt,
        }
      );
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  /**
   * MOCK: POST /publisher/releases/:id/submit
   *
   * The one function this whole feature exists for, and the one that must not lie.
   *
   * ORDER OF CHECKS IS THE CONTRACT. Every precondition is evaluated before the
   * provider is considered, so a caller cannot reach distribution by having a backend
   * configured while skipping payment verification. §17: "Do NOT submit a release to
   * LabelGrid before successful payment verification" and "Never trust frontend payment
   * state alone" — hence the check is on `payment.verifiedAt`, which only a server can
   * set, and NOT on `payment.status === 'paid'`, which a client can.
   *
   * Then, having passed every check a real deployment would also have to pass, it
   * throws DistributionUnavailableError with the blockers and the plan. No release
   * record is marked submitted, no external id is invented, and nothing is scheduled to
   * flip a status later.
   *
   * @throws SubmissionRefusedError when a precondition of the flow is unmet.
   * @throws DistributionUnavailableError when the flow is satisfied but the provider
   *   cannot be reached — which is always, in this build.
   */
  async submitRelease(input: {
    release: PublisherRelease;
    songs: Song[];
    /** @deprecated Publisher access now covers release submission. */
    payment?: PublisherPayment | null;
    /** Whether the account holds the Publisher plan. Read by the caller from the store. */
    entitled: boolean;
  }): Promise<PublisherRelease> {
    const { release, songs, entitled } = input;

    if (USE_MOCK_API) {
      assertMock('publisherApi.submitRelease');

      // §5 — the plan gates submission, not merely the screen.
      if (!entitled) {
        throw new SubmissionRefusedError(
          'no-entitlement',
          'The Publisher plan is required to submit a release.'
        );
      }

      // §28 — a release already sent must not be sent twice, and a retry must not
      // duplicate it. `externalId` is the proof of a real prior submission.
      if (release.externalId != null) {
        throw new SubmissionRefusedError(
          'already-submitted',
          'This release has already been submitted to the distributor.'
        );
      }

      // A release song must be commercially verifiable and backed by audio.
      for (const songId of release.songIds) {
        const song = songs.find((s) => s.id === songId);
        const blocker = song
          ? describePublisherSongBlocker(song)
          : `Song ${songId} is no longer available.`;
        if (blocker) {
          throw new SubmissionRefusedError(
            song && isUploadedSong(song) ? 'song-license-unverified' : 'no-audio',
            blocker
          );
        }
      }

      const plan = buildSubmissionPlan({ release, songs });
      const blockers = describeLabelGridBlockers();
      throw new DistributionUnavailableError(
        'This build cannot submit a release to LabelGrid. ' +
          blockers.map((b) => b.summary).join(' '),
        blockers,
        plan
      );
    }

    try {
      return await http.post<PublisherRelease>(`/publisher/releases/${release.id}/submit`, {
        release,
        songs,
      });
    } catch (error) {
      rethrowPublisherBackendError(error, { release, songs });
    }
  },

  /**
   * MOCK: GET /publisher/releases/:id/platforms → LabelGrid GET /queues/distro
   *
   * Returns an empty list. Per-outlet status is a provider fact — one row per
   * (release, outlet) with the provider's own `status` and `updated_at` — and there is
   * no provider here. §23/§26: no invented statuses, no fabricated timestamps.
   */
  async getReleasePlatforms(releaseId: string): Promise<PublisherReleasePlatform[]> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.getReleasePlatforms');
      return mockDelay([]);
    }
    try {
      return await http.get<PublisherReleasePlatform[]>(
        `/publisher/releases/${releaseId}/platforms`
      );
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  async getReleasePlatformCatalog(
    releaseId: string
  ): Promise<PublisherReleasePlatformCatalogEntry[]> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.getReleasePlatformCatalog');
      return mockDelay(publisherPlatformFixtures.catalogByReleaseId[releaseId] ?? []);
    }
    try {
      return await http.get<PublisherReleasePlatformCatalogEntry[]>(
        `/publisher/releases/${releaseId}/platform-catalog`
      );
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  async getReleasePlatformStats(
    releaseId: string,
    outletKey: string
  ): Promise<PublisherReleasePlatformStats> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.getReleasePlatformStats');
      return mockDelay(
        publisherPlatformFixtures.statsByReleaseId[releaseId]?.[outletKey] ??
          unavailablePlatformStats({
            releaseId,
            outletKey,
            note: 'No platform-level provider stats were seeded for this outlet.',
          })
      );
    }
    try {
      return await http.get<PublisherReleasePlatformStats>(
        `/publisher/releases/${releaseId}/platforms/${encodeURIComponent(outletKey)}/stats`
      );
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },

  /**
   * MOCK/TEMPORARY: GET /publisher/analytics
   *
   * Analytics was explicitly requested as a mock-backed preview surface before the backend route
   * exists. Unlike submission, validation, outlet status, or payout facts, this read is clearly
   * marked `source: 'mock'` and the fixture copy keeps every number at preview/reporting strength
   * instead of claiming provider-confirmed delivery or storefront-live status.
   *
   * So this call stays fixture-backed in normal frontend runtime for now, exactly like Financial:
   * it lets the Analytics UI be reviewed without falling through to a missing `/publisher/analytics`
   * route or reducing the aggregate `All` view to an unavailable placeholder.
   *
   * When the backend route is ready, replace this body with `http.get('/publisher/analytics')`
   * and keep the route contract unchanged.
   */
  async getPublisherAnalytics(input: {
    releaseId: string;
    preset: PublisherAnalyticsRangePreset;
    startDate?: string | null;
    endDate?: string | null;
    mode?: PublisherDataMode;
  }): Promise<PublisherAnalyticsPayload> {
    const mode = input.mode ?? (USE_MOCK_API ? 'mock' : 'real');
    if (mode === 'real') {
      try {
        return await http.get<PublisherAnalyticsPayload>('/publisher/analytics', {
          query: {
            releaseId: input.releaseId,
            preset: input.preset,
            ...(input.startDate ? { startDate: input.startDate } : {}),
            ...(input.endDate ? { endDate: input.endDate } : {}),
          },
        });
      } catch (error) {
        rethrowPublisherBackendError(error);
      }
    }
    if (USE_MOCK_API) assertMock('publisherApi.getPublisherAnalytics');
    const fixtureInput = isPublisherAnalyticsAllReleasesId(input.releaseId)
      ? { ...input, releaseId: PUBLISHER_ANALYTICS_ALL_RELEASES_ID }
      : input;
    return mockDelay(analyticsFixtureFor(fixtureInput));
  },

  /**
   * MOCK/TEMPORARY: GET /publisher/financial
   *
   * This screen was explicitly requested as a mocked reporting prototype backed by a committed
   * JSON fixture. Unlike submission, payment, outlet delivery or validation, this read does not
   * claim that anything happened in the outside world; the payload itself says `source: 'mock'`
   * and the route renders that fact on screen. Keeping it fixture-backed in normal frontend
   * runtime therefore satisfies the brief without fabricating a distribution-side side effect,
   * and it avoids blocking the Financial tab on a backend route that does not exist yet.
   *
   * When the backend is ready, replace this body with `http.get('/publisher/financial')` and
   * keep the route unchanged.
   */
  async getPublisherFinancial(
    options: PublisherModeOptions = {}
  ): Promise<PublisherFinancialPayload> {
    const mode = options.mode ?? (USE_MOCK_API ? 'mock' : 'real');
    if (mode === 'real') {
      try {
        return await http.get<PublisherFinancialPayload>('/publisher/financial');
      } catch (error) {
        rethrowPublisherBackendError(error);
      }
    }
    if (USE_MOCK_API) assertMock('publisherApi.getPublisherFinancial');
    return mockDelay(clonePublisherFinancialPayload(publisherFinancialFixture));
  },

  /**
   * REAL: GET /publisher/settings
   *
   * Publisher Settings now round-trips through the backend. In test-only mock mode this still
   * asserts if called without an API base URL, because the runtime configuration is expected to
   * point at the real backend whenever this settings surface is exercised.
   */
  async getPublisherSettings(): Promise<PublisherSettingsPayload> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.getPublisherSettings');
    }
    return http.get<PublisherSettingsPayload>('/publisher/settings');
  },

  async updatePublisherSettings(
    input: UpdatePublisherSettingsInput
  ): Promise<PublisherSettingsPayload> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.updatePublisherSettings');
    }
    return http.patch<PublisherSettingsPayload>('/publisher/settings', input);
  },

  /**
   * MOCK: POST /publisher/releases/:id/payment
   *
   * REJECTS. There is no payment provider in this project — productsApi's own header
   * says its purchases take no money — and a resolved promise here would be a $12 charge
   * the user believes happened. §18's "Payment Successful ✓" screen must never be
   * reached by a simulation.
   *
   * The idempotency key the caller generated is still the right design (§27), and the
   * UI's duplicate-click and duplicate-charge guards are implemented and tested around
   * this call, so they are in place the day a provider exists.
   */
  async payPublishingFee(input: {
    releaseId: string;
    paymentId: string;
    idempotencyKey: string;
  }): Promise<PublisherPaymentCheckoutSession> {
    if (USE_MOCK_API) {
      assertMock('publisherApi.payPublishingFee');
      throw new DistributionUnavailableError(
        `The $${PUBLISHING_FEE} publishing fee cannot be charged: this build has no payment ` +
          'provider, and no charge may be simulated. A backend must create the charge and verify ' +
          'it server-side before a release is submitted.',
        describeLabelGridBlockers()
      );
    }
    try {
      return await http.post<PublisherPaymentCheckoutSession>(
        `/publisher/releases/${input.releaseId}/payment`,
        {
          paymentId: input.paymentId,
          idempotencyKey: input.idempotencyKey,
        }
      );
    } catch (error) {
      rethrowPublisherBackendError(error);
    }
  },
};

/**
 * Re-exported so callers do not need both modules for the common case.
 *
 * The blockers and `canDistribute` belong to the LabelGrid contract, but every consumer
 * that asks "can I submit?" is already holding publisherApi.
 */
export { canDistribute, describeLabelGridBlockers, isSubmittable };
export type { LabelGridBlocker, LabelGridOutlet, PublisherReleaseType };
